import SanityImage from '@otovo/shared/components/Sanity/SanityImage';
import useHasMounted from '@otovo/shared/hooks/useHasMounted';
import dayjs from '@otovo/shared/lib/dayjs';
import { Sanity$BlogWithFeaturedPost } from '@otovo/shared/types/sanityTypes';

type Props = {
  content: Sanity$BlogWithFeaturedPost;
};

const BlogWithFeaturedPost = ({ content }: Props) => {
  // This component uses dayjs. Since we call dayjs' setLocale via a useEffect,
  // server-side rendering won't have the locale set and we'll get an hydration
  // error on the client. Workaround that by waiting to render the dayjs part
  // until we've mounted.
  const hasMounted = useHasMounted();
  return (
    <div
      className="color-scheme-dark bg-surface"
      data-testid="blog-with-featured-post"
    >
      <div className="mx-auto max-w-8xl px-4 py-16 md:px-6 md:py-20 lg:px-16 lg:py-24">
        <div className="flex grid-cols-12 flex-col lg:grid">
          <div className="order-2 col-span-6 space-y-6 lg:order-1 lg:mb-0">
            <div className="relative overflow-hidden">
              <SanityImage
                image={content.image}
                className="mr-auto mb-6 aspect-[3/2] rounded-xl object-cover shadow-2xl ring-1 ring-grey-90/10"
              />
              <a
                href={content.featuredPost.link}
                className="group block cursor-pointer"
              >
                <h3 className="mb-3 text-xl font-semibold text-pretty text-heading group-hover:text-heading/60 lg:text-2xl">
                  {content.featuredPost.title}
                </h3>
                <span className="text-body group-hover:text-body/60">
                  {hasMounted &&
                    dayjs(content.featuredPost.publishedAt).fromNow()}
                </span>
              </a>
            </div>
          </div>
          <div className="order-1 col-span-5 col-start-8 mb-20 md:mb-12 lg:order-2 lg:mb-0">
            <h2 className="mb-8 text-2xl font-semibold text-pretty text-heading md:text-3xl lg:text-4xl">
              {content.heading}
            </h2>
            <ol className="mb-10 space-y-6">
              {content.posts.map((blogpost) => (
                <li
                  key={blogpost._key}
                  className="group relative grid cursor-pointer grid-cols-[1fr_auto] gap-4 pr-8 pb-8"
                >
                  <a href={blogpost.link}>
                    <h3 className="mb-3 block text-xl font-semibold text-pretty text-heading group-hover:text-heading/60 lg:text-2xl">
                      {blogpost.title}
                    </h3>
                    <span className="text-body group-hover:text-body/60">
                      {hasMounted && dayjs(blogpost.publishedAt).fromNow()}
                    </span>
                  </a>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogWithFeaturedPost;
